import { parseProject, type Project } from '@finsim/model';
import { createEffect, createStore, sample } from 'effector';
import { projectClosed, sharedProjectOpened } from '../../entities/project';
import { bindViewQuery } from '../../features/view-query';
import { ApiRequestError } from '../../shared/api/http';
import { shares } from '../../shared/api/shares';
import { routes } from '../../shared/routing';

/*
 * A read-only share link (`/s/<token>`): loads the latest saved version of the project and shows
 * it read-only. Nothing is stored on this device; view state lives in the URL like on the
 * project page.
 */

export type SharedStatus = 'loading' | 'ready' | 'notFound' | 'outdated' | 'error';

class SharedLoadError extends Error {
  constructor(readonly status: Exclude<SharedStatus, 'loading' | 'ready'>) {
    super(status);
  }
}

export const loadSharedFx = createEffect(async (token: string): Promise<Project> => {
  let raw: unknown;
  try {
    raw = (await shares.read(token)).project;
  } catch (error) {
    throw new SharedLoadError(
      error instanceof ApiRequestError && error.status === 404 ? 'notFound' : 'error',
    );
  }
  const parsed = parseProject(raw);
  if (!parsed.success) {
    // A project saved by a newer version of the app: a reload fetches that version.
    throw new SharedLoadError(parsed.error === 'futureVersion' ? 'outdated' : 'error');
  }
  return parsed.data;
});

const $token = createStore<string | null>(null)
  .on([routes.shared.opened, routes.shared.updated], (_, { params }) => params.token)
  .reset(routes.shared.closed);

export const $sharedStatus = createStore<SharedStatus>('loading')
  .on(loadSharedFx, () => 'loading')
  .on(loadSharedFx.doneData, () => 'ready')
  .on(loadSharedFx.failData, (_, error) =>
    error instanceof SharedLoadError ? error.status : 'error',
  );

/** The token last requested: view-state changes update the route but don't reload. */
const $requested = createStore<string | null>(null)
  .on(loadSharedFx, (_, token) => token)
  .reset(routes.shared.closed);

sample({
  clock: [routes.shared.opened, routes.shared.updated],
  source: $requested,
  filter: (requested, { params }) => requested !== params.token,
  fn: (_, { params }) => params.token,
  target: loadSharedFx,
});

// Only the link still open is shown (a slow response for a previous link is dropped).
sample({
  clock: loadSharedFx.done,
  source: $token,
  filter: (token, { params }) => token === params,
  fn: (_, { result }) => result,
  target: sharedProjectOpened,
});

sample({ clock: routes.shared.closed, target: projectClosed });

bindViewQuery(routes.shared);
